import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, LockIcon, MailIcon, PhoneIcon, RefreshCwIcon, ShieldIcon } from 'lucide-react';

/**
 * DESIGN ONLY — visual treatment for the existing WebsiteForge enquiry form.
 *
 * IMPLEMENTATION NOTE: when this ships, the live form instance must be preserved as-is.
 * Keep the same form frame/embed, field names, input IDs, hidden inputs, routing/action URL,
 * tracking, notification and webhook wiring, reCAPTCHA instance, and submit behavior.
 * Only the surrounding markup, classes, and layout below should be applied.
 */

const budgets = ['Under $5k', '$5k – $15k', '$15k – $40k', '$40k+', 'Not sure yet'];
const nextSteps = [{
  title: 'We read it, not a bot',
  body: 'A person on our team reviews what you sent and looks at your current site.'
}, {
  title: 'A real conversation',
  body: 'We walk through how your business runs and where the site is getting in the way.'
}, {
  title: 'Scope and price first',
  body: 'You get a written plan — pages, integrations, timeline, cost — before anything is built.'
}];
export function GetStarted() {
  const [budget, setBudget] = useState('$5k – $15k');
  const [verified, setVerified] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  return <section id="get-started" className="relative overflow-hidden bg-ink py-16 lg:py-24" data-id="element-104" data-mp="1awsagkqswnsp">
      <div className="absolute inset-0 bp opacity-80" aria-hidden="true" data-id="element-105" data-mp="27mrtj94kv4pj" />
      <div className="absolute -right-24 bottom-0 h-[32rem] w-[32rem] rounded-full bg-forge-orange/[0.09] blur-[130px]" aria-hidden="true" data-id="element-106" data-mp="27m00o62d7l2y" />

      <div className="relative mx-auto grid max-w-8xl gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:gap-12 lg:px-10" data-id="element-107" data-mp="1ytxzg5kh20rm">
        <div className="lg:col-span-5" data-id="element-108" data-mp="1tw67jbjtuenf">
          <p className="flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.24em] text-forge-cyan" data-id="element-109" data-mp="1tv4n4ggx8ft1">
            <span className="h-px w-7 bg-forge-orange" aria-hidden="true" data-id="element-110" data-mp="2rclilffwr5q2" />
            Get started
          </p>
          <h2 className="mt-4 font-display text-[1.75rem] font-extrabold leading-[1.1] tracking-[-0.02em] text-white sm:text-[2.2rem]" data-id="element-111" data-mp="35gkfgti38drf">
            Tell us how your business works.
            <span className="block text-mist-300" data-id="element-112" data-mp="3ah85pcyhsqkl">We will tell you what we would build.</span>
          </h2>
          <p className="mt-5 max-w-lg text-[15px] leading-relaxed text-mist-200" data-id="element-113" data-mp="30pkk3soma3r1">
            Send the short version: what you sell, what the current site is not doing, and what needs to connect to it. We
            will come back with a straight answer on scope, timeline, and cost.
          </p>

          <ol className="mt-9 space-y-4" data-id="element-114" data-mp="2dp4kab2gtipl">
            {nextSteps.map((step, i) => <li key={step.title} className="flex gap-4" data-id="element-115" data-mp="2drhafbidmzdq" data-mpi={i}>
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-white/12 bg-white/[0.04] font-mono text-[11px] text-forge-orange" data-id="element-116" data-mp="2hrhnarptv2io">
                  0{i + 1}
                </span>
                <div data-id="element-117" data-mp="fytk2id6of2w">
                  <p className="text-[14px] font-bold text-white" data-id="element-118" data-mp="fx0mdskjt571">{step.title}</p>
                  <p className="mt-1 text-[13px] leading-relaxed text-mist-300" data-id="element-119" data-mp="20hni1it18dee">{step.body}</p>
                </div>
              </li>)}
          </ol>

          <div className="mt-9 grid gap-3 sm:grid-cols-2" data-id="element-120" data-mp="2a88qrz2f6yb1">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-id="element-121" data-mp="25ggy9izpeii9">
              <PhoneIcon className="h-4 w-4 text-forge-cyan" aria-hidden="true" data-id="element-122" data-mp="25edyw8cnvd0v" />
              <p className="mt-2.5 text-[13px] font-bold text-white" data-id="element-123" data-mp="2k3julmcail40">Phone support</p>
              <p className="mt-1 text-[12px] text-mist-400" data-id="element-124" data-mp="2f5icp4qloru4">Mon–Fri, business hours</p>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-id="element-125" data-mp="2t5jdkk6v7z9l">
              <MailIcon className="h-4 w-4 text-forge-cyan" aria-hidden="true" data-id="element-126" data-mp="2o81ahafj4e9i" />
              <p className="mt-2.5 text-[13px] font-bold text-white" data-id="element-127" data-mp="yr8nk8ejaz28">Email & tickets</p>
              <p className="mt-1 text-[12px] text-mist-400" data-id="element-128" data-mp="tt77zxgbhhvi">Answered by the build team</p>
            </div>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.5
      }} className="lg:col-span-7" data-id="element-129" data-mp="w6u7ib1mg7bj">
          <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-navy-950/85 p-6 shadow-lift sm:p-8" data-id="element-130" data-mp="1a7dc186vov8y">
            <span className="absolute inset-x-0 top-0 h-[3px] bg-forge-orange" aria-hidden="true" data-id="element-131" data-mp="ck4u1n6364bp" />
            <div className="absolute inset-0 bp-fine opacity-50" aria-hidden="true" data-id="element-132" data-mp="hhy1ejj4l4dk" />

            <div className="relative mb-6 flex items-center justify-between gap-4 border-b border-white/10 pb-5" data-id="element-133" data-mp="mi2gq68art27">
              <div data-id="element-134" data-mp="r8smvlojrzxy">
                <p className="font-display text-[15px] font-bold text-white" data-id="element-135" data-mp="r9asva10hsj2">Project enquiry</p>
                <p className="mt-1 text-[12px] text-mist-400" data-id="element-136" data-mp="3pxq38y8vz5y0">Takes about two minutes.</p>
              </div>
              <span className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 font-mono text-[9.5px] uppercase tracking-[0.16em] text-mist-300" data-id="element-137" data-mp="3uw1d38z6lgg8">
                <LockIcon className="h-3 w-3 text-forge-green" aria-hidden="true" data-id="element-138" data-mp="3igbhir31ddgq" />
                Secure
              </span>
            </div>

            <form className="relative" onSubmit={e => {
            e.preventDefault();
            setSubmitted(true);
          }} data-id="element-139" data-mp="3dhqd0fgr47mh">
              <div className="grid gap-5 sm:grid-cols-2" data-id="element-140" data-mp="34hbpd9gbegft">
                <Field id="name" name="name" label="Name" placeholder="Your full name" required data-id="element-141" data-mp="2znh8sew0ahky" />
                <Field id="email" name="email" type="email" label="Email" placeholder="you@company.com" required data-id="element-142" data-mp="2uqh4a8et1mvy" />
                <Field id="mobile" name="mobile" type="tel" label="Mobile number" placeholder="(000) 000-0000" required data-id="element-143" data-mp="2prw3o7vn516z" />
                <Field id="company" name="company" label="Company" placeholder="Business name" data-id="element-144" data-mp="2ktce7xhu979v" />
              </div>

              <fieldset className="mt-7" data-id="element-145" data-mp="264j6j1inn8xe">
                <legend className="text-[13px] font-semibold text-mist-100" data-id="element-146" data-mp="263hw8z9gqqgq">Budget</legend>
                <div className="mt-3 flex flex-wrap gap-2.5" data-id="element-147" data-mp="2b4p1aqk81bjp">
                  {budgets.map(b => {
                  const active = budget === b;
                  return <label key={b} className={`cursor-pointer rounded-xl border px-4 py-2.5 text-[13px] font-semibold transition-colors ${active ? 'border-forge-orange bg-forge-orange/15 text-white' : 'border-white/12 bg-white/[0.03] text-mist-200 hover:border-white/30'}`} data-id="element-148" data-mp="222fsq68ijs8y">
                        <input type="radio" name="budget" value={b} checked={active} onChange={() => setBudget(b)} className="sr-only" data-id="element-149" data-mp="2kzpcjohr37qq" />
                        <span className="flex items-center gap-2" data-id="element-150" data-mp="3ivb6arhm1727">
                          <span className={`grid h-3.5 w-3.5 place-items-center rounded-full border ${active ? 'border-forge-orange' : 'border-white/35'}`} aria-hidden="true" data-id="element-151" data-mp="3iv2s8w9r3vpf">
                            {active && <span className="h-1.5 w-1.5 rounded-full bg-forge-orange" data-id="element-152" data-mp="2zwgr5he8i4li" />}
                          </span>
                          {b}
                        </span>
                      </label>;
                })}
                </div>
              </fieldset>

              <div className="mt-7" data-id="element-153" data-mp="t60sw55nfexb">
                <label htmlFor="details" className="block text-[13px] font-semibold text-mist-100" data-id="element-154" data-mp="t57rzktdfjn3">
                  Project details <span className="text-forge-orange" data-id="element-155" data-mp="o64oi4t1hlh8">*</span>
                </label>
                <textarea id="details" name="details" rows={5} required placeholder="What do you sell, what is the current site doing wrong, and what does it need to connect to?" className="mt-2 w-full resize-none rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-[14.5px] leading-relaxed text-white placeholder:text-white/35 focus:border-forge-cyan focus:outline-none focus:ring-2 focus:ring-forge-cyan/30" data-id="element-156" data-mp="2io63c2etzwkl" />
              </div>

              {/* reCAPTCHA slot — the live reCAPTCHA instance mounts here */}
              <div className="mt-6 flex items-center justify-between gap-4 rounded-xl border border-white/12 bg-white/[0.03] px-4 py-4" data-id="element-157" data-mp="3achnb3ikmvm9">
                <label className="flex items-center gap-3 text-[13.5px] font-medium text-mist-100" data-id="element-158" data-mp="1g12v8g238k3c">
                  <input type="checkbox" checked={verified} onChange={e => setVerified(e.target.checked)} className="h-5 w-5 rounded border-white/30 bg-transparent text-forge-green focus:ring-forge-cyan" data-id="element-159" data-mp="1kykvzj5ybt09" />
                  I&apos;m not a robot
                </label>
                <div className="flex items-center gap-2" data-id="element-160" data-mp="yqyzligc5lsu">
                  <RefreshCwIcon className="h-4 w-4 text-mist-400" aria-hidden="true" data-id="element-161" data-mp="ysjsz4qwyyp4" />
                  <div className="text-right" data-id="element-162" data-mp="1wdegoj4uohfv">
                    <p className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-mist-300" data-id="element-163" data-mp="1rfmpjrc14zcq">reCAPTCHA</p>
                    <p className="text-[9.5px] text-white/40" data-id="element-164" data-mp="1mg9syevcwdwy">Privacy · Terms</p>
                  </div>
                </div>
              </div>

              <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center" data-id="element-165" data-mp="1ieernadfe0j6">
                <button type="submit" className="inline-flex items-center justify-center rounded-xl bg-forge-orange px-7 py-4 text-[15px] font-bold text-ink shadow-ember transition-transform hover:-translate-y-0.5" data-id="element-166" data-mp="b2fnssy2uqw0">
                  Let&apos;s Get Started
                </button>
                <p className="flex items-start gap-2 text-[12.5px] leading-snug text-mist-300" data-id="element-167" data-mp="2t5qqtcucdwah">
                  <ShieldIcon className="mt-0.5 h-4 w-4 shrink-0 text-forge-green" aria-hidden="true" data-id="element-168" data-mp="371b8y628yagu" />
                  Your details stay with our team. No lists, no resale.
                </p>
              </div>

              {submitted && <p role="status" className="mt-5 flex items-start gap-2.5 rounded-xl border border-forge-green/30 bg-forge-green/10 px-4 py-3.5 text-[13px] leading-relaxed text-white" data-id="element-169" data-mp="3byupxdqbx1ch">
                  <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-forge-green" aria-hidden="true" data-id="element-170" data-mp="3kzj4qanjr5qf" />
                  Thanks — this is a design preview, so nothing was submitted. On the live site this posts to the existing
                  WebsiteForge form and we reply the same business day.
                </p>}
            </form>
          </div>
        </motion.div>
      </div>
    </section>;
}
type FieldProps = {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  type?: string;
  required?: boolean;
};
function Field({
  id,
  name,
  label,
  placeholder,
  type = 'text',
  required
}: FieldProps) {
  return <div data-id="element-171" data-mp="3ji3vswzmmhws">
      <label htmlFor={id} className="block text-[13px] font-semibold text-mist-100" data-id="element-172" data-mp="3jhaz6oudt8sd">
        {label}
        {required && <span className="ml-1 text-forge-orange" data-id="element-173" data-mp="3oh702f7s8k3p">*</span>}
      </label>
      <input id={id} name={name} type={type} required={required} placeholder={placeholder} className="mt-2 w-full rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-[14.5px] text-white placeholder:text-white/35 focus:border-forge-cyan focus:outline-none focus:ring-2 focus:ring-forge-cyan/30" data-id="element-174" data-mp="3ahfh002esdiu" />
    </div>;
}